import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "Can depression be treated without more medication?",
    answer:
      "Yes. Options include psychotherapy, traditional TMS, accelerated TMS (SAINT®), and other non-systemic approaches. Some patients may still appropriately use medication as part of treatment. The right approach depends on your history and should be discussed with a psychiatrist.",
  },
  {
    question: "Is TMS a medication?",
    answer:
      "No. TMS is a medical device treatment, not a drug. It uses magnetic pulses to stimulate a specific brain region and introduces no chemical into the body. TMS devices are FDA-cleared, not FDA-approved (the term used for medications). Many people continue an antidepressant during TMS, but TMS itself can be delivered without medication when a clinician judges that appropriate.",
  },
  {
    question: "Can I stop my antidepressant if I start TMS?",
    answer:
      "Not on your own. Stopping abruptly can cause discontinuation symptoms and raise the risk of relapse. If you want to reduce or stop medication, ask your prescriber to plan a gradual taper and to decide the timing relative to TMS. TMS is commonly delivered alongside medication.",
  },
  {
    question: "What non-medication treatments are available?",
    answer:
      "Non-medication options include psychotherapy (CBT, behavioral activation, interpersonal therapy), traditional TMS, accelerated TMS (SAINT®), and lifestyle support as adjunctive care. ECT is also non-medication but is reserved for severe cases. Each has different evidence, risks, and suitability.",
  },
  {
    question: "Is it anti-medication to consider these options?",
    answer:
      "No. Considering non-medication options is not anti-medication. Some patients benefit from medication; others prefer or need alternatives. Many treatment plans combine medication and non-medication approaches. The goal is to find what works for you, guided by a psychiatrist.",
  },
];

export const pageBeyondMedication: LearnPageData = {
  slug: "depression-treatment-without-more-medication",
  title: "Depression Treatment Beyond More Medication | Neuro Wellness",
  description:
    "Depression treatment beyond adding another medication: psychotherapy, TMS, SAINT®, and non-systemic approaches. Some patients may still use medication. Not anti-medication.",
  canonical: "/learn/depression-treatment-without-more-medication",
  h1: "Depression Treatment Options Beyond Adding Another Medication",
  breadcrumbLabel: "Depression Treatment Beyond Medication",
  crumbs: baseCrumbs("Depression Treatment Beyond Medication"),
  shortAnswer:
    "If you have tried multiple antidepressants and are looking for options beyond adding another medication, several evidence-based approaches exist. These include psychotherapy, traditional TMS, accelerated TMS (SAINT®), and other non-systemic treatments that do not involve adding another drug. Some patients may still appropriately use medication as part of a comprehensive plan — considering these options is not anti-medication. This page explains the non-medication options available and clarifies that medication may still play a role in your treatment.",
  keyTakeaways: [
    "Options exist beyond adding another medication.",
    "Psychotherapy, TMS, and SAINT® are non-medication approaches.",
    "Some patients may still appropriately use medication.",
    "Considering alternatives is not anti-medication.",
    "A psychiatric evaluation guides the right approach.",
  ],

  sections: [
    {
      heading: "Psychotherapy",
      body: (
        <>
          {P(
            "Psychotherapy — including CBT, behavioral activation, and interpersonal therapy — is effective for depression and does not involve medication. It may be used alongside medication or on its own. Therapy addresses underlying patterns, skills, and behaviors. If you have not tried therapy, it may be worth adding to your plan.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/services/psychotherapy"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                psychotherapy
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Lifestyle Support as Adjunctive Care",
      body: (
        <>
          {P(
            "Regular exercise, consistent sleep, daylight exposure, reduced alcohol, and social contact have evidence for depressive symptoms. These are adjunctive — helpful additions rather than stand-alone treatments for moderate-to-severe depression. They support overall treatment but are not a substitute for clinical care when symptoms are significant.",
          )}
        </>
      ),
    },
    {
      heading: "Traditional TMS",
      body: (
        <>
          {P(
            "Traditional TMS is FDA-cleared for adults with major depressive disorder who have not improved on antidepressant medication. It is a medical device treatment — not a drug — that uses magnetic pulses to stimulate the prefrontal cortex. It is delivered one session per day for about six weeks and is broadly covered by insurance.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/what-is-tms-therapy-for-depression"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                TMS therapy
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Accelerated TMS (SAINT®)",
      body: (
        <>
          {P(
            "SAINT® is an FDA-cleared accelerated TMS protocol delivered over five consecutive days with fMRI-guided targeting. Like traditional TMS, it is a device treatment, not a drug. It is faster in delivery and typically self-pay. Individual response varies.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/what-is-saint-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                SAINT® TMS
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Other Non-Systemic Treatment Approaches",
      body: (
        <>
          {P(
            "Other non-systemic approaches include Spravato (esketamine), which is a medication but acts differently from oral antidepressants, and ECT for severe cases. These are not 'non-medication' in the strict sense but may be considered when oral antidepressants have not worked. Discuss all options with your psychiatrist.",
          )}
        </>
      ),
    },
    {
      heading: "Medication May Still Play a Role",
      body: (
        <>
          {P(
            "Considering non-medication options is not anti-medication. Some patients benefit from medication; others prefer or need alternatives. Many treatment plans combine medication and non-medication approaches — for example, continuing an antidepressant during TMS. The goal is to find what works for you, guided by a psychiatrist. Do not stop medication on your own.",
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "Cuijpers P, et al. The effects of psychotherapy for adult depression are overestimated. Psychol Med. 2014;44(1):211-223.",
      url: "https://pubmed.ncbi.nlm.nih.gov/23432814/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/depression-treatment-without-more-medication#webpage`,
        name: "Depression Treatment Options Beyond Adding Another Medication",
        url: `${SITE_URL}/learn/depression-treatment-without-more-medication`,
        description:
          "Depression treatment beyond more medication: psychotherapy, TMS, SAINT®, and non-systemic approaches. Medication may still play a role.",
      }),
      orgNode,
      faqJsonld(
        faqs,
        `${SITE_URL}/learn/depression-treatment-without-more-medication#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("Depression Treatment Beyond Medication")),
    ],
  },
  relatedLinks: [
    ...sharedRelated,
    {
      title: "When Antidepressants Don't Work",
      href: "/learn/when-antidepressants-dont-work",
      description: "What to try next",
    },
    {
      title: "Depression Treatment Without Medication",
      href: "/learn/depression-treatment-without-medication",
      description: "Evidence-based non-medication treatments",
    },
  ],
};
